import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ActionCardComponent } from '@shared/components/action-card/action-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { SituacaoVendedorEnum } from '@shared/enums/pessoa/situacao-vendedor.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { OportunidadeResumoStruct } from '@shared/services/structs/vendas/oportunidade-resumo.struct';

/** Documento de Vendas gravado (orçamento ou pedido) que recebe o vínculo. */
export type VincularOportunidadeDialogData = {
  idVenda: number;
  idVendaTipoDocumento: number;
  numero: number;
  idPessoaCliente: number;
  nomeCliente: string;
  /** Vindo do "Vincular a ela" do Criar oportunidade: essa oportunidade já abre marcada. */
  idOportunidadeSugerida?: number | null;
};

/** A oportunidade vinculada, para quem abriu atualizar a marca e o menu sem recarregar. */
export type VincularOportunidadeDialogResult = {
  oportunidade: OportunidadeResumoStruct;
};

type OportunidadeAbertaLinha = OportunidadeResumoStruct & { nomeVendedor: string };

/**
 * "Vincular a oportunidade existente" (RX-02): oportunidades abertas do cliente do documento, dentro do escopo
 * de quem vincula. O dialog grava o vínculo e devolve a oportunidade escolhida; as recusas vêm da API.
 */
@Component({
  selector: 'app-vincular-oportunidade-dialog',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule, MatRadioModule,
    MatTooltipModule, ActionCardComponent, EmptyStateComponent, LoadingBoxDefaultListComponent],
  templateUrl: './vincular-oportunidade-dialog.component.html',
  styleUrls: ['./vincular-oportunidade-dialog.component.css'],
})
export class VincularOportunidadeDialogComponent implements OnInit {
  public oportunidades: OportunidadeAbertaLinha[] = [];
  public idOportunidadeSelecionada: number | null = null;
  public isLoading = true;
  public isVinculando = false;

  constructor(
    private dialogRef: MatDialogRef<VincularOportunidadeDialogComponent, VincularOportunidadeDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: VincularOportunidadeDialogData,
    private processoVendaService: ProcessoVendaService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.carregarOportunidades();
  }

  /** "Orçamento nº 12 · Cliente": o tipo como o usuário fala, como no protótipo. */
  public get subtitulo(): string {
    const tipo = this.data.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda ? 'Pedido' : 'Orçamento';
    return `${tipo} nº ${this.data.numero} · ${this.data.nomeCliente}`;
  }

  public selecionar(oportunidade: OportunidadeAbertaLinha): void {
    if (this.isVinculando) return;
    this.idOportunidadeSelecionada = oportunidade.idOportunidade;
  }

  public fechar(): void {
    if (this.isVinculando) return;
    this.dialogRef.close();
  }

  public vincular(): void {
    const oportunidade = this.oportunidades.find(o => o.idOportunidade === this.idOportunidadeSelecionada);
    if (!oportunidade || this.isVinculando) return;

    this.isVinculando = true;
    this.processoVendaService.vincularOportunidade(this.data.idVenda, oportunidade.idOportunidade).subscribe({
      next: response => {
        this.isVinculando = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso',
          `Documento vinculado à oportunidade #${oportunidade.codigo}. O evento fica no histórico da oportunidade.`,
          AlertType.success);
        this.dialogRef.close({ oportunidade });
      },
      error: () => {
        this.isVinculando = false;
        this.alertService.show('Aviso',
          'Não foi possível vincular o documento agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private carregarOportunidades(): void {
    forkJoin({
      response: this.processoVendaService.getOportunidadesAbertasDoCliente(this.data.idPessoaCliente),
      // Sem a lista de vendedores a consulta continua, com "—" no vendedor.
      vendedores: this.pessoaLookupService.listVendedor().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ response, vendedores }) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        // Mesmo nome do dialog "Oportunidades do cliente": vendedor inativo continua aparecendo, com a marca.
        const nomesPorId = new Map<number, string>(
          (vendedores && !vendedores.isError ? vendedores.list ?? [] : [])
            .map(v => [v.idVendedor,
              v.idSituacaoVendedor === SituacaoVendedorEnum.Inativo ? `${v.nome} (inativo)` : v.nome]));

        this.oportunidades = (response.oportunidades ?? []).map(oportunidade => ({
          ...oportunidade,
          nomeVendedor: nomesPorId.get(oportunidade.idVendedorResponsavel) ?? '—',
        }));

        const sugerida = this.oportunidades.find(o => o.idOportunidade === this.data.idOportunidadeSugerida);
        this.idOportunidadeSelecionada = sugerida?.idOportunidade ?? null;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar as oportunidades do cliente. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }
}
